/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { http, HttpResponse, passthrough } from 'msw';
import type { AirmetFromBackend, SigmetFromBackend } from '@opengeoweb/api';
import { airmetConfig, sigmetConfig } from '@opengeoweb/sigmet-airmet';
import airmetListJSON from './mockAIRMETList.json';
import sigmetListJSON from './mockSIGMETList.json';

export const airmetList = airmetListJSON as unknown as AirmetFromBackend[];
export const sigmetList = sigmetListJSON as unknown as SigmetFromBackend[];

export const fakeSigmetTAC =
  'EHAA SIGMET T01 VALID 121249/121449 EHDB- EHAA AMSTERDAM FIR FRQ TS OBS ENTIRE FIR FL010 STNR WKN';

export const fakeAirmetTAC =
  'EHAA AIRMET -1 VALID 310700/311100 EHDB- EHAA AMSTERDAM FIR ISOL CB OBS ENTIRE FIR FL020 STNR NC';

export const fakeDraftCancelSigmetTAC =
  'EHAA SIGMET T02 121249/121449 EHDB- EHAA AMSTERDAM FIR';

export const fakeDraftCancelAirmetTAC =
  'EHAA AIRMET 2  310700/311100 EHDB- EHAA AMSTERDAM FIR ';
export const aviationEndpoints = [
  http.get(/^.*\/airmetlist$/, ({ request }) => {
    const url = new URL(request.url);
    const startTime = url.searchParams.get('start_time');
    const endTime = url.searchParams.get('end_time');

    let filtered = airmetList;

    if (startTime && endTime) {
      const start = new Date(startTime);
      const end = new Date(endTime);

      filtered = airmetList.filter((airmet) => {
        const validStart = new Date(airmet.airmet.validDateStart);
        const validEnd = new Date(airmet.airmet.validDateEnd);
        return validStart <= end && validEnd >= start;
      });
    }
    return HttpResponse.json(filtered);
  }),
  http.get(/^.*\/sigmetlist$/, ({ request }) => {
    const url = new URL(request.url);
    const startTime = url.searchParams.get('start_time');
    const endTime = url.searchParams.get('end_time');

    let filtered = sigmetList;

    if (startTime && endTime) {
      const start = new Date(startTime);
      const end = new Date(endTime);

      filtered = sigmetList.filter((sigmet) => {
        const validStart = new Date(sigmet.sigmet.validDateStart);
        const validEnd = new Date(sigmet.sigmet.validDateEnd);
        return validStart <= end && validEnd >= start;
      });
    }
    return HttpResponse.json(filtered);
  }),
  http.get(/^.*\/airmet-config$/, () => {
    return HttpResponse.json(airmetConfig);
  }),
  http.get(/^.*\/sigmet-config$/, () => {
    return HttpResponse.json(sigmetConfig);
  }),
  http.post(/^.*\/airmet2tac$/, () => {
    return HttpResponse.text(fakeAirmetTAC);
  }),
  http.post(/^.*\/sigmet2tac$/, () => {
    return HttpResponse.text(fakeSigmetTAC);
  }),
  http.post(/^.*\/airmet2cancel2tac$/, () => {
    return HttpResponse.text(fakeDraftCancelAirmetTAC);
  }),
  http.post(/^.*\/sigmet2cancel2tac$/, () => {
    return HttpResponse.text(fakeDraftCancelSigmetTAC);
  }),
  http.post(/^.*\/airmet$/, () => {
    return HttpResponse.json({});
  }),
  http.post(/^.*\/sigmet$/, async () => {
    return HttpResponse.json({
      uuid: 'mock-uuid-123',
    });
  }),
  http.get(/^(?!.*(sigmet|airmet)).*$/, () => {
    return passthrough();
  }),
];
